import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/hooks/use-auth';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';

/**
 * The account owner's id for the signed-in user, resolved once and shared.
 *
 * Account-scoped tables store rows under `resolve_account_owner(actor)`, never
 * the raw session id — a shared-account member's own `user.id` matches nothing.
 * Every analytics hook that queries such a table needs this id first.
 *
 * Why it is a shared hook rather than five copies: the query key is what makes
 * the resolution happen ONCE for a whole page instead of once per hook. Before
 * this, `['account-owner-id', user?.id]` was hand-typed in six separate files.
 * That works only while all six strings stay byte-identical — one typo and the
 * offending hook silently gets its own cache entry and fires its own RPC, with
 * no error and nothing visibly wrong. Naming it once removes the chance.
 *
 * ⚠️ Not every table wants this. 34 tables are USER-scoped (`chat_sessions`,
 * `ai_messages`, `usage_events`, `user_profiles`, …) where the raw `user.id` is
 * correct and resolving is an actual bug. Check
 * `src/lib/identity/table-scope.generated.ts` for the table you are querying —
 * correctness is a property of (row, table), never of the id alone.
 */
export function useResolvedOwnerId(enabled = true) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ['account-owner-id', user?.id],
    queryFn: () => resolveOwnerId(user!.id),
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000,
  });
}
